import type { TFunction } from "i18next";
import type { SchemaElement } from "../../../api/restaurantSchema";
import type { TableStatus } from "../../../components/restaurant-schema/tableTones";
import { formatClock, minutesBetween } from "../logic/dayTime";
import { reservationStartMs, type TableState } from "../logic/tableStatus";
import type { ReservationFormat } from "../hooks/useReservationFormat";

/**
 * Състоянието на масата → декорацията ѝ на сцената:
 * - заета — розово-червен плот, „⏱ 35 мин" (от колко време е заета към T);
 * - резервирана — кремав плот с пунктир, „⏱ 19:00", точка „!" при закъснение;
 * - свободна — бял плот и чип „⏱ 21:45" с часа на следващата резервация.
 */
export const floorTableStatus = (
  state: TableState,
  momentMs: number,
  highlighted: boolean,
  format: ReservationFormat,
): TableStatus => {
  if (state.kind === "occupied") {
    return {
      tone: "occupied",
      badge: {
        text: format.duration(minutesBetween(state.sinceMs, momentMs)),
        compactText: format.compactDuration(minutesBetween(state.sinceMs, momentMs)),
        icon: "clock",
      },
      alert: state.hasLateArrival,
      highlighted,
    };
  }
  if (state.kind === "reserved") {
    return {
      tone: "reserved",
      badge: { text: formatClock(reservationStartMs(state.reservation)), icon: "clock" },
      alert: state.hasLateArrival,
      highlighted,
    };
  }
  return {
    tone: "free",
    chip: state.next ? formatClock(reservationStartMs(state.next)) : undefined,
    alert: state.hasLateArrival,
    highlighted,
  };
};

/** Hover title: „Маса 1 · 5 места · резервирана 19:00 · Николай Георгиев". */
export const floorTableTitle = (
  table: SchemaElement,
  state: TableState,
  momentMs: number,
  format: ReservationFormat,
  t: TFunction,
): string => {
  const base = t("reservations.table.hover", {
    name: table.name ?? "",
    seats: format.seats(table.seats ?? 0),
  });
  if (state.kind === "reserved") {
    const detail = t("reservations.table.hoverReserved", {
      time: formatClock(reservationStartMs(state.reservation)),
      guest: state.reservation.guestName,
    });
    return `${base} · ${detail}`;
  }
  if (state.kind === "occupied") {
    const detail = t("reservations.table.hoverOccupied", {
      duration: format.duration(minutesBetween(state.sinceMs, momentMs)),
    });
    const guest = state.reservation ? ` · ${state.reservation.guestName}` : "";
    return `${base} · ${detail}${guest}`;
  }
  if (state.next) {
    const detail = t("reservations.table.hoverNext", {
      time: formatClock(reservationStartMs(state.next)),
    });
    return `${base} · ${detail}`;
  }
  return base;
};
